/**
 * Checks if localStorage is available and has sufficient permissions
 * @returns boolean indicating if localStorage is available
 */
export function hasLocalStorage() {
  try {
    const hasReadWritePermission = () => {
      localStorage.setItem("test", "test");
      localStorage.removeItem("test");
      return true;
    };

    return typeof window !== "undefined" && window.localStorage != null && hasReadWritePermission();
  } catch (error) {
    return false;
  }
}

/**
 * Gets an item from localStorage with type safety
 * @param key - Storage key to retrieve
 * @returns Parsed value from storage or null if not found
 */
export function getFromLocalStorage<T>(key: string): T | null {
  if (!hasLocalStorage()) {
    console.warn("localStorage is not available");
    return null;
  }

  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  } catch (error) {
    console.error("Error retrieving from localStorage:", error);
    return null;
  }
}

/**
 * Saves an item to localStorage with type safety
 * @param key - Storage key to save under
 * @param value - Value to store
 * @returns boolean indicating success
 */
export function saveToLocalStorage<T>(key: string, value: T): boolean {
  if (!hasLocalStorage()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    const serialized = JSON.stringify(value);
    window.localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error("Error saving to localStorage:", error);
    return false;
  }
}

/**
 * Removes an item from localStorage
 * @param key - Storage key to remove
 * @returns boolean indicating success
 */
export function removeFromLocalStorage(key: string): boolean {
  if (!hasLocalStorage()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error("Error removing from localStorage:", error);
    return false;
  }
}

/**
 * Clears all items from localStorage
 * @returns boolean indicating success
 */
export function clearLocalStorage(): boolean {
  if (!hasLocalStorage()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    window.localStorage.clear();
    return true;
  } catch (error) {
    console.error("Error clearing localStorage:", error);
    return false;
  }
}
